/**
 * Spotlight + companion cards hero — a large feature card plus two
 * smaller cards stacked next to it. Ported from ThemeMaker's
 * {@code blocks/hero/HeroSpotlightCards}.
 *
 * <p>Layout: 7/5 (or 5/7) split between the feature and the stacked
 * pair, controlled by {@code bigSide}. Collapses to a single column
 * under 960px.
 */
import { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { tx } from '../_shared/coerce';
import { sectionInner, muted, kickerStyle, cardReset } from '../_shared/styles';
import { Button, Card } from '@akademi/ui';
import {
  PortalLink,
  isVisible,
  useCurrentUser,
  type CurrentUser,
} from '@akademi/site-builder-runtime';
import type { CampaignAudience } from '../_shared/campaignAudience';
import { HeroEmptyFallback } from '../_shared/HeroEmptyFallback';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

interface SpotlightFeature {
  kicker?: Localized;
  title?: Localized;
  description?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  image?: string;
  badges?: Localized[];
  meta?: Array<{ label: Localized; value: Localized }>;
}

interface SpotlightCard extends CampaignAudience {
  kicker?: Localized;
  title?: Localized;
  subtitle?: Localized;
  image?: string;
  href?: string;
  badge?: Localized;
}

export interface HeroSpotlightCardsProps {
  bigSide?: 'left' | 'right';
  background?: 'tinted' | 'plain';
  feature?: SpotlightFeature;
  items?: SpotlightCard[];
  /** 'full' (default) lets content span the section edge-to-edge;
   *  'theme' constrains it to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
}

export function HeroSpotlightCards({
  bigSide = 'right',
  background = 'tinted',
  feature,
  items = [],
  maxWidth = 'full',
}: HeroSpotlightCardsProps) {
  const currentUser = useCurrentUser<CurrentUser>();
  const small = useMemo(
    () => items.filter((it) => isVisible(it, currentUser)).slice(0, 2),
    [items, currentUser],
  );

  // Big feature card — proportionally larger than FeatureSpotlight's.
  // 480px min-height, font-size-4xl title, more padding.
  const FeatureCard = (
    <Card
      style={{
        ...cardReset,
        display: 'flex',
        flexDirection: 'column',
        background: feature?.image
          ? `linear-gradient(135deg, rgba(15,17,24,0.85), rgba(15,17,24,0.45)), url(${feature.image}) center/cover`
          : 'linear-gradient(135deg, var(--color-text-primary, #18181b), var(--color-neutral-700, #3f3f46))',
        color: 'var(--color-text-on-brand, #fff)',
        padding: 'var(--space-10, 40px)',
        borderRadius: 'var(--radius-lg, 12px)',
        minHeight: 480,
        gap: 'var(--space-4, 16px)',
      }}
    >
      {feature?.badges && feature.badges.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--space-1-5, 6px)', flexWrap: 'wrap' }}>
          {feature.badges.map((b, i) => (
            <span
              key={i}
              style={{
                padding: 'var(--space-1, 4px) var(--space-2-5, 10px)',
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 'var(--font-weight-semibold, 600)',
                fontFamily: 'var(--font-family-body, inherit)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--font-tracking-widest, 0.08em)',
                background: i === 0 ? 'rgba(255,255,255,0.15)' : 'var(--color-brand-500, #3b5bdb)',
                borderRadius: 'var(--radius-sm, 4px)',
              }}
            >
              {tx(b)}
            </span>
          ))}
        </div>
      )}
      <div
        style={{
          fontSize: 'clamp(32px, 4vw, 56px)',
          fontWeight: 'var(--font-weight-bold, 600)',
          lineHeight: 'var(--font-leading-tight, 1.2)',
          fontFamily: 'var(--font-family-display, inherit)',
          marginTop: feature?.badges?.length ? 'var(--space-2, 8px)' : 0,
          letterSpacing: 'var(--font-tracking-snug, -0.02em)',
        }}
      >
        {tx(feature?.title) || 'Featured spotlight'}
      </div>
      {feature?.description && (
        <div
          style={{
            fontSize: 'var(--font-size-lg, 18px)',
            opacity: 0.85,
            lineHeight: 'var(--font-leading-relaxed, 1.5)',
            maxWidth: 520,
          }}
        >
          {tx(feature.description)}
        </div>
      )}
      {feature?.meta && feature.meta.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-8, 32px)',
            flexWrap: 'wrap',
            marginTop: 'auto',
          }}
        >
          {feature.meta.map((m, i) => (
            <div key={i}>
              <div
                style={{
                  fontSize: 'var(--font-size-xs, 12px)',
                  fontWeight: 'var(--font-weight-semibold, 600)',
                  fontFamily: 'var(--font-family-body, inherit)',
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--font-tracking-widest, 0.1em)',
                  opacity: 0.6,
                }}
              >
                {tx(m.label)}
              </div>
              <div
                style={{
                  marginTop: 'var(--space-1, 4px)',
                  fontSize: 'var(--font-size-md, 16px)',
                  fontWeight: 'var(--font-weight-medium, 500)',
                }}
              >
                {tx(m.value)}
              </div>
            </div>
          ))}
        </div>
      )}
      {feature?.ctaLabel && (
        <Button
          href={feature.ctaHref ?? '#'}
          radius="pill"
          style={{
            alignSelf: 'flex-start',
            height: 'auto',
            gap: 'var(--space-2, 8px)',
            padding: 'var(--space-3, 12px) var(--space-5, 20px)',
            // Pill sits on a dark/colored card; lock contrast.
            background: 'var(--color-neutral-0, #fff)',
            color: 'var(--color-text-primary, #18181b)',
            fontSize: 'var(--font-size-md, 16px)',
            fontWeight: 'var(--font-weight-bold, 600)',
            marginTop: feature?.meta?.length ? 0 : 'auto',
          }}
        >
          {tx(feature.ctaLabel)} <ArrowRight aria-hidden size={18} />
        </Button>
      )}
    </Card>
  );

  // Two smaller cards. Stacked next to the feature card on desktop;
  // once the section collapses to one column, they go side-by-side on
  // tablet widths and back to stacked on narrow mobile.
  const ItemList = (
    <div className="hero-spotlight-cards-items" style={{ display: 'grid', gap: 16 }}>
      {(small.length ? small : ([{}, {}] as SpotlightCard[])).map((it, i) => (
        <Card asChild key={i}>
          <PortalLink
            href={it.href ?? '#'}
            style={{
              ...cardReset,
              display: 'flex',
              position: 'relative',
              background: 'var(--color-surface-card, #fff)',
              borderRadius: 'var(--radius-md, 8px)',
              textDecoration: 'none',
              color: 'inherit',
              overflow: 'hidden',
              minHeight: 232,
              flexDirection: 'column',
            }}
          >
            {!small.length && <HeroEmptyFallback style={{ position: 'absolute', inset: 0 }} />}
            <div
              style={{
                height: 130,
                flexShrink: 0,
                background: it.image
                  ? `url(${it.image}) center/cover`
                  : 'linear-gradient(135deg, var(--color-brand-50, #eef2ff), var(--color-brand-100, #e0e7ff))',
              }}
            />
            <div
              style={{
                flex: 1,
                padding: 'var(--space-5, 20px)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2, 8px)',
              }}
            >
              {it.kicker && (
                <div style={{ ...kickerStyle, fontSize: 'var(--font-size-xs, 12px)' }}>
                  {tx(it.kicker)}
                </div>
              )}
              {it.badge && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignSelf: 'flex-start',
                    padding: 'var(--space-0-5, 2px) var(--space-2-5, 10px)',
                    fontSize: 'var(--font-size-xs, 12px)',
                    fontWeight: 'var(--font-weight-medium, 500)',
                    border: '1px solid var(--color-border-default, #e5e7eb)',
                    borderRadius: 'var(--radius-pill, 9999px)',
                    color: 'var(--color-text-secondary, #5f6675)',
                  }}
                >
                  {tx(it.badge)}
                </span>
              )}
              <div
                style={{
                  fontSize: 'var(--font-size-lg, 18px)',
                  fontWeight: 'var(--font-weight-semibold, 600)',
                  lineHeight: 'var(--font-leading-tight, 1.2)',
                  color: 'var(--color-text-primary, #0f1218)',
                }}
              >
                {tx(it.title) || 'Card title'}
              </div>
              {it.subtitle && <div style={{ ...muted, marginTop: 'auto' }}>{tx(it.subtitle)}</div>}
            </div>
          </PortalLink>
        </Card>
      ))}
    </div>
  );

  return (
    <BlockRoot
      as="section"
      style={{
        padding: 'var(--space-12, 48px) var(--space-6, 24px) var(--space-16, 64px)',
        background:
          background === 'tinted'
            ? 'var(--color-brand-50, #f0f4ff)'
            : 'var(--color-surface-page, #f7f8fa)',
        ...(maxWidth === 'theme'
          ? {
              maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
              width: '100%',
              margin: '0 auto',
            }
          : {}),
      }}
    >
      <div
        style={{
          ...sectionInner,
          maxWidth: 'none',
        }}
      >
        <div
          className="hero-spotlight-cards-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: bigSide === 'left' ? '7fr 5fr' : '5fr 7fr',
            gap: 'var(--space-6, 24px)',
            alignItems: 'stretch',
          }}
        >
          {bigSide === 'left' ? (
            <>
              {FeatureCard}
              {ItemList}
            </>
          ) : (
            <>
              {ItemList}
              {FeatureCard}
            </>
          )}
        </div>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (max-width: 960px) {
          .hero-spotlight-cards-grid {
            grid-template-columns: 1fr !important;
          }
        }

        /* Tablet: once the section is one column, the two supporting
           cards have room to sit side-by-side. */
        @media (max-width: 960px) and (min-width: 641px) {
          .hero-spotlight-cards-items {
            grid-template-columns: 1fr 1fr;
          }
        }

        /* Mobile: back to stacked. */
        @media (max-width: 640px) {
          .hero-spotlight-cards-items {
            grid-template-columns: 1fr;
          }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}
